import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import moment from "moment";
import axios from "axios";
import { saveAs } from "file-saver";
import { resolveEntityId, sortInvoicesByNumberDesc } from "../../utils/utils";
import {
  Typography,
  Table,
  useTheme,
  TableBody,
  TableCell,
  TableHead,
  TableContainer,
  TableFooter,
  TablePagination,
  TableRow,
  Paper,
  Container,
  Button,
  CircularProgress,
  createTheme,
  ThemeProvider,
  IconButton,
  makeStyles,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
} from "@material-ui/core";
import { getComplimentaryInvoices } from "../../actions/invoiceActions";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import FirstPageIcon from "@material-ui/icons/FirstPage";
import KeyboardArrowLeft from "@material-ui/icons/KeyboardArrowLeft";
import KeyboardArrowRight from "@material-ui/icons/KeyboardArrowRight";
import LastPageIcon from "@material-ui/icons/LastPage";
import ClearIcon from "@material-ui/icons/Clear";
import GetAppIcon from "@material-ui/icons/GetApp";
import { MuiPickersUtilsProvider, KeyboardDatePicker } from "@material-ui/pickers";
import DateFnsUtils from "@date-io/date-fns";
import PropTypes from "prop-types";

const colorTheme = createTheme({
  palette: {
    primary: {
      main: "#ef7f1a",
    },
  },
});

const useStyles1 = makeStyles((theme) => ({
  root: {
    flexShrink: 0,
    marginLeft: theme.spacing(2.5),
  },
}));

function TablePaginationActions(props) {
  const classes = useStyles1();
  const theme = useTheme();
  const { count, page, rowsPerPage, onPageChange } = props;

  const handleFirstPageButtonClick = (event) => {
    onPageChange(event, 0);
  };

  const handleBackButtonClick = (event) => {
    onPageChange(event, page - 1);
  };

  const handleNextButtonClick = (event) => {
    onPageChange(event, page + 1);
  };

  const handleLastPageButtonClick = (event) => {
    onPageChange(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1));
  };

  return (
    <div className={classes.root}>
      <IconButton
        onClick={handleFirstPageButtonClick}
        disabled={page === 0}
        aria-label="first page"
      >
        {theme.direction === "rtl" ? <LastPageIcon /> : <FirstPageIcon />}
      </IconButton>
      <IconButton
        onClick={handleBackButtonClick}
        disabled={page === 0}
        aria-label="previous page"
      >
        {theme.direction === "rtl" ? (
          <KeyboardArrowRight />
        ) : (
          <KeyboardArrowLeft />
        )}
      </IconButton>
      <IconButton
        onClick={handleNextButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="next page"
      >
        {theme.direction === "rtl" ? (
          <KeyboardArrowLeft />
        ) : (
          <KeyboardArrowRight />
        )}
      </IconButton>
      <IconButton
        onClick={handleLastPageButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="last page"
      >
        {theme.direction === "rtl" ? <FirstPageIcon /> : <LastPageIcon />}
      </IconButton>
    </div>
  );
}

TablePaginationActions.propTypes = {
  count: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
  page: PropTypes.number.isRequired,
  rowsPerPage: PropTypes.number.isRequired,
};

const tableStyle = {
  width: 160,
  fontSize: 14,
  cursor: "pointer",
  borderBottom: "none",
  padding: "8px",
  textAlign: "center",
};
const headerStyle = { borderBottom: "none", textAlign: "center" };

const complimentaryStatusStyle = {
  border: "solid 0px #4caf50",
  backgroundColor: "#e8f5e9",
  padding: "8px 18px",
  borderRadius: "20px",
  color: "#2e7d32",
  fontWeight: "500",
};

const ComplimentaryHistory = () => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const history = useHistory();
  const { complimentaryInvoices, isLoading } = useSelector((state) => state.invoices);
  const user = JSON.parse(localStorage.getItem("profile"));

  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // Search and filter states
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedFilter, setSelectedFilter] = useState("");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    dispatch(getComplimentaryInvoices());
  }, [dispatch]);

  if (!user) {
    history.push("/login");
  }

  const toCommas = (value) => {
    return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFilterChange = (event) => {
    setSelectedFilter(event.target.value);
  };

  const handleStartDateChange = (date) => {
    setStartDate(date);
  };

  const handleEndDateChange = (date) => {
    setEndDate(date);
  };

  const filterByDateRange = (invoice) => {
    if (!startDate || !endDate) return true;
    const invoiceDate = moment(invoice.createdAt);
    return invoiceDate.isBetween(startDate, endDate, "day", "[]");
  };

  // Get the actual invoices data from the API response
  const invoicesData = complimentaryInvoices?.data || [];
  
  const filteredRows = sortInvoicesByNumberDesc(
    invoicesData.filter((row) => {
    const isInvoiceNumberFilter = selectedFilter === "invoiceNumber";
    const searchQueryLowerCase = searchQuery && typeof searchQuery === "string" ? searchQuery.toLowerCase() : "";

    if (isInvoiceNumberFilter) {
      const invoiceNumber = row.invoiceNumber?.toLowerCase() || "";
      return filterByDateRange(row) && invoiceNumber.includes(searchQueryLowerCase);
    } else {
      const clientName = (row?.client?.name || "Cash").toLowerCase();
      return filterByDateRange(row) && clientName.includes(searchQueryLowerCase);
    }
  })
  );

  const createAndDownloadPdf = () => {
    setLoading(true);
    if (filteredRows.length) {
      axios
        .post(`${process.env.REACT_APP_API}/create-report`, {
          filteredRows,
          users: [],
          isComplimentaryInvoices: true, // Updated flag for complimentary invoices
          profile: user.userProfile,
          fromDate: startDate,
          toDate: endDate,
        })
        .then(() =>
          axios.get(`${process.env.REACT_APP_API}/fetch-report`, {
            responseType: "blob",
          })
        )
        .then((res) => {
          const pdfBlob = new Blob([res.data], { type: "application/pdf" });
          const currentDate = new Date();
          saveAs(pdfBlob, `ComplimentaryInvoicesReport${currentDate}.pdf`);
          const downloadUrl = `${process.env.REACT_APP_URL}/files/Exported-Reports/Report.pdf`;
          window.open(downloadUrl, "_blank");
          setLoading(false);
        })
        .catch((error) => {
          console.error("Error generating PDF:", error);
          setLoading(false);
          alert("Error generating PDF report. Please try again.");
        });
    } else {
      alert("No records to export");
    }
  };

  const openInvoice = (id) => {
    const invoiceId = resolveEntityId(id);
    if (!invoiceId) return;
    history.push(`/invoice/${invoiceId}`);
  };

  if (isLoading) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexDirection: "column",
          paddingTop: "20px",
        }}
      >
        <CircularProgress />
      </div>
    );
  }

  return (
    <ThemeProvider theme={colorTheme}>
      <Container>
        <div style={{ marginTop: "20px", marginBottom: "20px" }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => history.push("/invoices")}
            style={{ marginBottom: "20px" }}
          >
            Back to Invoices
          </Button>
          <Typography variant="h4" gutterBottom>
            Complimentary Invoices History
          </Typography>
        </div>

        <div style={{ marginBottom: "20px" }}>
          <div style={{ display: "flex", gap: "20px", marginBottom: "20px" }}>
            <MuiPickersUtilsProvider utils={DateFnsUtils}>
              <KeyboardDatePicker
                margin="normal"
                label="Start Date"
                format="dd/MM/yyyy"
                value={startDate}
                onChange={handleStartDateChange}
                style={{ width: "200px" }}
              />
              <KeyboardDatePicker
                margin="normal"
                label="End Date"
                format="dd/MM/yyyy"
                value={endDate}
                onChange={handleEndDateChange}
                style={{ width: "200px" }}
              />
            </MuiPickersUtilsProvider>

            <FormControl style={{ width: "200px", marginTop: "16px" }}>
              <InputLabel>Filter:</InputLabel>
              <Select
                value={selectedFilter}
                onChange={handleFilterChange}
              >
                <MenuItem value="invoiceNumber">Filter by Invoice Number</MenuItem>
                <MenuItem value="customer">Filter by Customer</MenuItem>
              </Select>
              {selectedFilter && (
                <IconButton
                  size="small"
                  style={{
                    marginLeft: "8px",
                    backgroundColor: "transparent",
                    color: "#ef7f1a",
                    borderRadius: "4px",
                  }}
                  onClick={() => {
                    setSelectedFilter("");
                    setSearchQuery("");
                  }}
                >
                  <ClearIcon />
                </IconButton>
              )}
            </FormControl>

            {selectedFilter && (
              <TextField
                variant="outlined"
                label={`Enter ${selectedFilter === "invoiceNumber" ? "Invoice Number" : "Customer Name"}`}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{ width: "200px", marginTop: "8px" }}
              />
            )}
{/* 
            {loading ? (
              <CircularProgress style={{ marginTop: "20px" }} />
            ) : (
              <Button
                variant="outlined"
                endIcon={<GetAppIcon />}
                onClick={createAndDownloadPdf}
                style={{
                  marginTop: "16px",
                  height: "50px",
                }}
              >
                PDF
              </Button>
            )} */}
          </div>

          {((startDate && endDate) || searchQuery) && (
            <div style={{ margin: "10px 0" }}>
              <Typography variant="h6">
                Complimentary Invoices Count: {filteredRows.length}
              </Typography>
            </div>
          )}
        </div>

        <TableContainer component={Paper} elevation={0}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell style={headerStyle}>Invoice Number</TableCell>
                <TableCell style={headerStyle}>Date</TableCell>
                <TableCell style={headerStyle}>Customer</TableCell>
                <TableCell style={headerStyle}>Payment Mode</TableCell>
                <TableCell style={headerStyle}>Amount</TableCell>
                <TableCell style={headerStyle}>Status</TableCell>
                <TableCell style={headerStyle}>Created At</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(rowsPerPage > 0
                ? filteredRows.slice(
                    page * rowsPerPage,
                    page * rowsPerPage + rowsPerPage
                  )
                : filteredRows
              ).map((invoice) => (
                <TableRow
                  key={invoice.id}
                  style={{ cursor: "pointer" }}
                  onClick={() => openInvoice(invoice.id)}
                >
                  <TableCell style={tableStyle}>
                    {invoice.invoiceNumber}
                  </TableCell>
                  <TableCell style={tableStyle}>
                    {moment(invoice.createdAt).format("MMM Do YYYY")}
                  </TableCell>
                  <TableCell style={tableStyle}>
                    {invoice?.client?.name || "Cash"}
                  </TableCell>
                  <TableCell style={tableStyle}>
                    {invoice.paymentMode || "N/A"}
                  </TableCell>
                  <TableCell style={tableStyle}>
                    {invoice.currency}{" "}
                    {invoice.total
                      ? toCommas(invoice.total.toFixed(2))
                      : invoice.total}
                  </TableCell>
                  <TableCell style={tableStyle}>
                    <button
                      style={complimentaryStatusStyle}
                      onClick={(e) => {
                        e.stopPropagation();
                        openInvoice(invoice.id);
                      }}
                    >
                      Complimentary
                    </button>
                  </TableCell>
                  <TableCell style={tableStyle}>
                    {moment(invoice.createdAt).format("MMM Do YYYY, h:mm a")}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
            <TableFooter>
              <TableRow>
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25, { label: "All", value: -1 }]}
                  colSpan={7}
                  count={filteredRows.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  SelectProps={{
                    inputProps: { "aria-label": "rows per page" },
                    native: true,
                  }}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  ActionsComponent={TablePaginationActions}
                />
              </TableRow>
            </TableFooter>
          </Table>
        </TableContainer>
      </Container>
    </ThemeProvider>
  );
};

export default ComplimentaryHistory;